// Översikt (admin mockup §1): what needs a person today, straight from the ledger.

import type { Overview } from "../api/client.js";
import { t } from "../i18n.js";
import type { AdminView } from "./AdminShell.jsx";

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
};

function describeDeviation(p: Overview["deviations"]["latest"][number]["payload"]): string {
  const d = p.detail ?? p.disposition ?? "";
  const what = d === "unknown-product" ? t("dev.unknownProduct") : d === "missing-lot" ? t("dev.missingLot") : d === "missing-best-before" ? t("dev.missingBestBefore") : d === "quantity-unknown" ? t("dev.quantityUnknown") : d === "tally" ? t("banner.tally") : d;
  const ids = [p.gtin ? `GTIN ${p.gtin}` : null, p.lot ? `lot ${p.lot}` : null, p.reason ? `${p.reason}` : null].filter(Boolean).join(" · ");
  return ids ? `${what} · ${ids}` : what;
}

export function OverviewView({ overview, onGo }: { overview: Overview | null; onGo: (v: AdminView) => void }) {
  const now = new Date();
  const o = overview;
  const ageOfOldest = o?.putAway.oldest ? null : null;
  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.overview")}</h1><div className="crumb">{now.toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {now.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</div></div>
      </div>
      <div className="adm-content">
        {!o && <p className="ahint">{t("common.loading")}</p>}
        {o && (
          <>
            <div className="tiles">
              <button type="button" className={`tile${o.deviations.last30Days > 0 ? " warn" : ""}`}><div className="k">{t("adm.tile.deviations")}</div><div className="v">{o.deviations.last30Days}</div><div className="d">{t("adm.tile.deviations.d")}</div></button>
              <button type="button" className="tile"><div className="k">{t("adm.tile.putaway")}</div><div className="v">{o.putAway.waiting}</div><div className="d">{o.putAway.oldest?.locationCode ? t("adm.tile.putaway.d", { code: o.putAway.oldest.locationCode }) : t("adm.tile.putaway.none")}{ageOfOldest ?? ""}</div></button>
              <button type="button" className="tile"><div className="k">{t("adm.tile.deliveries")}</div><div className="v">{o.receipts.length}</div><div className="d">{o.receipts.slice(0, 3).map((r) => r.reference).join(" · ") || t("adm.tile.deliveries.none")}</div></button>
              <button type="button" className="tile ok" onClick={() => onGo("devices")}><div className="k">{t("adm.tile.currency")}</div><div className="v">{o.ledger.lastAt ? fmtTime(o.ledger.lastAt) : "—"}</div><div className="d">{t("adm.tile.currency.d", { n: o.ledger.lastSequence, devices: o.devices.filter((d) => d.enrolled).length })}</div></button>
            </div>
            <div className="adm-cols">
              <div className="card adm-grow">
                <div className="hd">{t("adm.deviations.title")} <span className="sub">{t("adm.latestFirst")}</span></div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>{t("adm.col.when")}</th><th>{t("adm.col.what")}</th><th>{t("adm.col.who")}</th><th>{t("adm.col.disposition")}</th></tr></thead>
                    <tbody>
                      {o.deviations.latest.length === 0 && <tr><td colSpan={4} className="mut">{t("adm.deviations.none")}</td></tr>}
                      {o.deviations.latest.map((d) => (
                        <tr key={d.sequence}>
                          <td className="id mut">{fmtTime(d.at)}</td>
                          <td>{describeDeviation(d.payload)}</td>
                          <td className="mut">{d.actorId.replace(/^user:/, "")}</td>
                          <td><span className="pill warn">{d.payload.disposition ?? ""}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card adm-w380">
                <div className="hd">{t("adm.today")}</div>
                <div className="bd">
                  <dl className="kv">
                    <dt>{t("adm.today.received")}</dt><dd className="id">{o.today["received"] ?? 0}</dd>
                    <dt>{t("adm.today.aggregated")}</dt><dd className="id">{o.today["aggregated"] ?? 0}</dd>
                    <dt>{t("adm.today.moved")}</dt><dd className="id">{o.today["moved"] ?? 0}</dd>
                    <dt>{t("adm.today.inspected")}</dt><dd className="id">{o.today["inspected"] ?? 0}</dd>
                    <dt>{t("adm.today.devices")}</dt><dd>{o.devices.filter((d) => d.enrolled).map((d) => d.name).join(" · ") || "—"}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
